import React, { useState, type ReactNode } from 'react';
import {
  Pressable,
  StyleSheet,
  Text,
  TextInput,
  View,
  type StyleProp,
  type ViewStyle,
} from 'react-native';

import { colors, radii, spacing, typography } from '../../../core/theme/tokens';
import { numberPadInputProps } from '../../../components/layout/keyboard';
import { GoogleIcon } from './GoogleIcon';

type GoogleSignInButtonProps = {
  onPress: () => void;
  disabled?: boolean;
  loading?: boolean;
  style?: StyleProp<ViewStyle>;
};

export function GoogleSignInButton({
  onPress,
  disabled,
  loading,
  style,
}: GoogleSignInButtonProps) {
  const [hovered, setHovered] = useState(false);

  return (
    <Pressable
      accessibilityRole="button"
      accessibilityLabel="Continue with Google"
      disabled={disabled}
      onPress={onPress}
      onHoverIn={() => setHovered(true)}
      onHoverOut={() => setHovered(false)}
      style={({ pressed }) => [
        styles.pressable,
        disabled && styles.disabled,
        (pressed || hovered) && !disabled && styles.interactive,
        style,
      ]}
    >
      <View style={styles.inner}>
        <GoogleIcon size={18} />
        <Text style={styles.label}>{loading ? 'Connecting…' : 'Continue with Google'}</Text>
      </View>
    </Pressable>
  );
}

type AuthPhoneFieldProps = {
  dialCode: string;
  value: string;
  onChangeText: (text: string) => void;
  editable?: boolean;
};

export function AuthPhoneField({
  dialCode,
  value,
  onChangeText,
  editable = true,
}: AuthPhoneFieldProps) {
  return (
    <View style={styles.phoneShell}>
      <View style={styles.dial}>
        <Text style={styles.dialText}>{dialCode}</Text>
      </View>
      <View style={styles.phoneDivider} />
      <TextInput
        style={styles.phoneInput}
        value={value}
        onChangeText={onChangeText}
        placeholder="Phone number"
        placeholderTextColor={colors.textMuted}
        {...numberPadInputProps('phone')}
        autoComplete="tel"
        textContentType="telephoneNumber"
        maxLength={15}
        editable={editable}
      />
    </View>
  );
}

export function AuthSheetSection({
  children,
  style,
}: {
  children: ReactNode;
  style?: StyleProp<ViewStyle>;
}) {
  return <View style={[styles.section, style]}>{children}</View>;
}

const styles = StyleSheet.create({
  pressable: {
    alignSelf: 'stretch',
    borderRadius: radii.pill,
    borderWidth: 1,
    borderColor: 'rgba(61, 214, 164, 0.38)',
    backgroundColor: 'rgba(61, 214, 164, 0.12)',
    minHeight: 52,
    justifyContent: 'center',
    // web
    cursor: 'pointer',
    transitionProperty: 'transform, box-shadow, border-color, background-color',
    transitionDuration: '160ms',
  } as ViewStyle,
  disabled: {
    opacity: 0.45,
  },
  interactive: {
    opacity: 0.95,
    transform: [{ translateY: -1 }],
    borderColor: 'rgba(255, 255, 255, 0.22)',
    backgroundColor: 'rgba(255, 255, 255, 0.07)',
  },
  inner: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    gap: spacing.sm,
    paddingHorizontal: spacing.lg,
    paddingVertical: spacing.md,
  },
  label: {
    ...typography.headline,
    fontSize: 16,
    color: colors.text,
  },
  phoneShell: {
    flexDirection: 'row',
    alignItems: 'center',
    minHeight: 52,
    borderRadius: radii.lg,
    borderWidth: 1,
    borderColor: 'rgba(255, 255, 255, 0.12)',
    backgroundColor: 'rgba(255, 255, 255, 0.04)',
    overflow: 'hidden',
  },
  dial: {
    paddingHorizontal: spacing.md,
    justifyContent: 'center',
    minHeight: 52,
  },
  dialText: {
    ...typography.body,
    color: colors.text,
    fontWeight: '600',
    fontVariant: ['tabular-nums'],
  },
  phoneDivider: {
    width: StyleSheet.hairlineWidth,
    alignSelf: 'stretch',
    backgroundColor: 'rgba(255, 255, 255, 0.12)',
    marginVertical: 12,
  },
  phoneInput: {
    flex: 1,
    ...typography.body,
    color: colors.text,
    paddingHorizontal: spacing.md,
    paddingVertical: spacing.md,
    minHeight: 52,
    outlineStyle: 'none',
  } as object,
  section: {
    gap: spacing.md,
  },
});
